<template>
  <div :data-testid="`backup-jobs-list-${direction}`" class="mt-2">
    <div class="coro-filter-content">
      <cui-filter-wrapper
        :show-clear-button="showClearFiltersButton"
        @clear-filters-clicked="clearFilters()"
      >
        <v-text-field
          v-model.trim="localFilters.search"
          :data-testid="`backup-jobs-${direction}-search`"
          class="search-field mr-1"
          :class="{ 'cui-filter-active': localFilters.search?.length }"
          density="compact"
          variant="outlined"
          :placeholder="$t('general.search')"
          :aria-label="$t('general.search')"
          prepend-inner-icon="icon-search-xs"
          @click:clear="localFilters.search = ''"
          rounded
          clearable
          hide-details
          clear-icon="$x"
        />
        <v-select
          v-model="localFilters.status"
          :items="statusOptions"
          class="cui-filter-item mr-1"
          :class="{ 'cui-filter-active': localFilters.status }"
          :placeholder="$t('cloudBackupAndRestore.filters.status')"
          :data-testid="`backup-jobs-${direction}-status-filter`"
          density="compact"
          variant="outlined"
          rounded
          hide-details
        >
          <template #selection="{ item }">
            {{ $t(`cloudBackupAndRestore.jobsTable.statuses.${item.value}`) }}
          </template>
          <template #item="{ item, props }">
            <v-list-item
              v-bind="props"
              :title="$t(`cloudBackupAndRestore.jobsTable.statuses.${item.value}`)"
            />
          </template>
        </v-select>
        <date-range-picker
          v-model="localFilters.timeFrame"
          :preset-dates="presets"
          :placeholder="$t('cloudBackupAndRestore.filters.timeSpan')"
          class="range-picker"
        />
      </cui-filter-wrapper>
    </div>

    <backup-jobs-table
      :items="jobs.items"
      :pagination="jobs.pagination"
      :total-items="jobs.total"
      :loading="jobs.loading"
      @page-changed="onPageChanged($event)"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType } from "vue";
import isEqual from "lodash/isEqual";
import { CuiFilterWrapper } from "@coronet/cui";
import { useI18n } from "vue-i18n";
import BackupJobsTable from "@/components/tables/BackupJobsTable.vue";
import DateRangePicker from "@/components/DateRangePicker.vue";
import { JOB_FILTER_CONTEXTS, useCloudBackupStore } from "@/_store/cloud-backup.module";
import { useFilters } from "@/composables/useFilters";
import { BACKUP_AND_RESTORE_STATUS_ORDER, BackupJobDirection } from "@/constants/cloud-backup.ts";
import { getCurrentDate, getNDaysAgo, getYesterday } from "@/constants/date-range-picker";
import type { Pagination } from "@/types";

export default defineComponent({
  components: {
    BackupJobsTable,
    CuiFilterWrapper,
    DateRangePicker,
  },
  props: {
    direction: {
      type: String as PropType<BackupJobDirection>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();
    const cloudBackupStore = useCloudBackupStore();

    const jobs = computed(() => cloudBackupStore.jobs[props.direction]);

    // Built through the date helpers rather than as module constants: those are evaluated
    // once at import time, so "Today" would point at the wrong day in a tab left open
    // past midnight.
    const presets = computed(() => [
      {
        label: i18n.t("dateRangePicker.today"),
        value: [getCurrentDate(), getCurrentDate()],
      },
      {
        label: i18n.t("dateRangePicker.yesterday"),
        value: [getYesterday(), getYesterday()],
      },
      {
        label: i18n.t("dateRangePicker.lastSevenDays"),
        value: [getNDaysAgo(6), getCurrentDate()],
      },
      {
        label: i18n.t("dateRangePicker.lastThirtyDays"),
        value: [getNDaysAgo(29), getCurrentDate()],
      },
    ]);

    const { localFilters, showClearFiltersButton, clearFilters } = useFilters({
      context: JOB_FILTER_CONTEXTS[props.direction],
      searchKey: "search",
      onFilterChange: () => {
        // A narrower result set can leave the current page out of range, so start over.
        cloudBackupStore.resetPagination(props.direction);
        return cloudBackupStore.getJobs(props.direction);
      },
    });

    const onPageChanged = (pagination: Pagination) => {
      // The table announces its options once on mount and again whenever the bound page
      // changes, so ignore the echoes that match what was already requested.
      if (isEqual(pagination, jobs.value.pagination)) {
        return;
      }
      cloudBackupStore.setPagination(props.direction, pagination);
      return cloudBackupStore.getJobs(props.direction);
    };

    onMounted(() => {
      // The table's own first `update:options` cannot serve as the initial fetch here: it
      // reports exactly the page already in the store, so the echo guard above discards it
      // and the tab would sit on an empty table. Fetching here instead keeps it to one
      // request — the guard is what stops the echo turning it into two.
      cloudBackupStore.getJobs(props.direction);
    });

    return {
      jobs,
      presets,
      localFilters,
      showClearFiltersButton,
      clearFilters,
      onPageChanged,
      statusOptions: BACKUP_AND_RESTORE_STATUS_ORDER,
    };
  },
});
</script>

<style scoped lang="scss">
.range-picker {
  width: 245px;
}

.search-field {
  min-width: 250px;
}
</style>
